// NowQuantum Brand Constants

export const BRAND = {
  /** @deprecated Use bgSurface2. */
  charcoal: "#2C2C2C",       // Surface 2 (was #2A2A2A)
  /** @deprecated Use bgBase. */
  nearBlack: "#121212",       // Canvas (was #111111)
  /** @deprecated Use brandSecondary. */
  deepRoyal: "#0F2B5B",       // Brand Secondary · Royal Blue (unchanged)
  /** @deprecated Use brandPrimary. */
  steel: "#5C8ECA",           // Brand Primary · Steel Blue (was #4A7AB5)
  black: "#000000",
  white: "#FFFFFF",

  border: "#333333",          // Dark-mode border (was #CCCCCC, which was a light-mode value)
  /** @deprecated Use statusSuccess. */
  success: "#4CAF50",         // Status: Nominal (was #0F6E56)
  /** @deprecated Use statusWarning. */
  warning: "#FFB74D",         // Status: Caution (was #BA7517)
  /** @deprecated Use statusDanger. */
  danger: "#CF6679",          // Status: Danger (was #A32D2D)

  /** @deprecated Use brandTertiary. */
  foresight: "#8B5CF6",       // Brand Tertiary · Foresight Purple (was #7024C0)

  // Text opacity levels
  textBright: "rgba(255,255,255,0.92)",
  textStandard: "rgba(255,255,255,0.85)",
  textMid: "rgba(255,255,255,0.80)",
  textSubtle: "rgba(255,255,255,0.75)",
  textDim: "rgba(255,255,255,0.70)",
  textMuted: "rgba(255,255,255,0.60)",

  tagline: "Mapping the Quantum-Safe Future",
  technicalOneLiner:
    "The control plane for Quantum-Safe critical infrastructure.",

  // Canonical brand-guide tokens (use these names in new code):
  brandPrimary: "#5C8ECA",
  brandSecondary: "#0F2B5B",
  brandTertiary: "#8B5CF6",
  bgBase: "#121212",
  bgSurface: "#1E1E1E",
  bgSurface2: "#2C2C2C",
  textPrimary: "#FAFAFA",     // AAA
  textSecondary: "#A0A0A0",   // AA
  statusSuccess: "#4CAF50",
  statusWarning: "#FFB74D",
  statusDanger: "#CF6679",
} as const;

// Light-mode palette — reserved for print, white papers, future light-mode UI.
// Currently unused in the dark-mode marketing site per brand guide § 2.2.
export const LIGHT_MODE = {
  bgBase: "#F8F9FA",
  bgSurface: "#FFFFFF",
  bgSurface2: "#F1F3F4",
  textPrimary: "#1A1A1A",
  textSecondary: "#5F6368",
  border: "#E0E0E0",
  brandPrimary: "#0F2B5B",      // In light mode, Royal Blue is primary
  brandSecondary: "#5C8ECA",    // and Steel Blue is secondary
  brandTertiary: "#7024C0",     // Deep Purple
  statusSuccess: "#2E7D32",
  statusWarning: "#C2410C",
  statusDanger: "#B3261E",
} as const;

export const SPACING = {
  "2xs": "4px",   // micro-gaps: icon-to-label, digit pairs
  xs: "8px",      // inline spacing, tight element gaps
  sm: "16px",     // headline-to-subtext, card internal gaps, form field spacing
  md: "24px",     // card padding, element group spacing
  lg: "32px",     // sub-section gaps, trust-signal-to-CTA gap
  xl: "48px",     // between content blocks within a section
  "2xl": "64px",  // minor section breaks, flow connector padding
  section: "96px",    // standard section vertical padding
  sectionLg: "128px", // hero and CTA section vertical padding
} as const;

export const GRADIENTS = {
  royalFade: "linear-gradient(180deg, #0F2B5B 0%, #1a1a2e 60%, #1e1440 100%)",
  royalToPurple: "linear-gradient(180deg, #0F2B5B 0%, #1e1440 40%, #2a1545 100%)",
  purpleAtmosphere: "linear-gradient(180deg, #1a0f28 0%, #2a1545 40%, #1e1440 100%)",
} as const;

export const CAPABILITIES = [
  {
    name: "SentinelQ",
    fullName: "FS-QSFA SentinelQ",
    verb: "Assess & Guard",
    description:
      "Continuous risk scoring that identifies your cryptographic vulnerabilities and prioritizes what to fix first.",
    color: BRAND.steel,
    href: "/sentinelq",
    image: "/site-images/sentinel-hero.webp",
  },
  {
    name: "PathfinderQ",
    fullName: "FS-QSFA PathfinderQ",
    verb: "Simulate & Plan",
    description:
      "Model Quantum-Safe deployment strategies across multiple combinations before committing resources.",
    color: BRAND.steel,
    href: "/pathfinderq",
    image: "/site-images/pathfinder-hero.webp",
  },
  {
    name: "CompassQ",
    fullName: "FS-QSFA CompassQ",
    verb: "Orchestrate",
    description:
      "Orchestrate QKD, PQC, and classical encryption across vendors and network layers from a single control plane.",
    color: BRAND.steel,
    href: "/compassq",
    image: "/site-images/compass-hero.webp",
  },
  {
    name: "ForesightQ",
    fullName: "FS-QSFA ForesightQ",
    verb: "Predict",
    description:
      "Detect threats, predict failures, and automate responses within the operational guardrails you define.",
    color: BRAND.steel,
    href: "/foresightq",
    image: "/site-images/foresight-hero.webp",
  },
] as const;

export const SECTORS: { name: string; description: string; href: string; image: string }[] = [
  {
    name: "Defense & Intelligence",
    description:
      "Quantum-Safe protection for sovereign networks, classified communications, and allied interoperability. Aligned with CCCS and allied readiness standards.",
    href: "/sectors/defense",
    image: "/site-images/sector-defense-img-1.webp",
  },
  {
    name: "Banking & Financial Services",
    description:
      "Protect financial infrastructure against harvest-now, decrypt-later attacks. Supports PCI DSS 4.0 and DORA compliance.",
    href: "/sectors/banking",
    image: "/site-images/sector-bank-img-1.webp",
  },
  {
    name: "Telecommunications",
    description:
      "Quantum-Safe encryption across 5G core, fiber backbones, and satellite uplinks with high-availability requirements.",
    href: "/sectors/telecommunications",
    image: "/site-images/sector-telco-img-1.webp",
  },
  {
    name: "Energy & Utilities",
    description:
      "Protect SCADA and OT networks from quantum threats to critical infrastructure. Supports NERC CIP compliance.",
    href: "/sectors/energy",
    image: "/site-images/sector-energy-img-1.webp",
  },
  {
    name: "Healthcare",
    description:
      "Quantum-Safe protection for healthcare infrastructure. Supports PIPEDA, provincial health information acts, and CCCS PQC compliance.",
    href: "/sectors/healthcare",
    image: "/site-images/sector-healthcare-img-1.webp",
  },
];

// Partners hidden temporarily
// export const PARTNERS = [
//   "Nokia",
//   "Crypto4A",
//   "Aegiq",
//   "TELUS",
//   "Université de Sherbrooke",
// ] as const;

export const PRINCIPLES = [
  {
    key: "principles.continuousAssessment",
  },
  {
    key: "principles.simulateFirst",
  },
  {
    key: "principles.vendorNeutral",
  },
  {
    key: "principles.operatorGuardrails",
  },
] as const;


export type NavLink = {
  label: string;
  key: string;
  href?: string;
  desc?: string;
  descKey?: string;
  children?: NavLink[];
};

export const NAV_LINKS: NavLink[] = [
  { label: "Home", key: "header.nav.home", href: "/" },
  { label: "About Us", key: "header.nav.about",    href: "/about" },
  { label: "Contact",  key: "header.nav.contact",  href: "/contact" },
];
